import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  SESSION_REVOKED_EVENT,
  SESSION_EXPIRED_EVENT,
  resetSessionExpirationHandling,
} from '@/utils/api-client';
import { sessionAuthService } from '@/services/session-auth-service';
import { getFreshIdTokenClaims } from '@/lib/auth0-id-token';

// When the backend reports the session as revoked (OIDC backchannel logout,
// admin-forced logout, password change), drive a full Auth0 /v2/logout so
// the SSO cookie is cleared — otherwise the SPA will silently
// re-authenticate and land the user right back in a new session.
function SessionRevokedListener() {
  const { logout } = useAuth0();

  useEffect(() => {
    const handler = () => {
      logout({ logoutParams: { returnTo: window.location.origin } });
    };

    window.addEventListener(SESSION_REVOKED_EVENT, handler);
    return () => window.removeEventListener(SESSION_REVOKED_EVENT, handler);
  }, [logout]);

  return null;
}

// When a Prosci admin's backend session cookie expires (but is NOT revoked),
// re-mint it from the still-valid Auth0 session in place — no page reload — so
// unsaved work survives. The re-mint hits the same /api/auth/token endpoint the
// original login used, so the server re-runs full admin validation (this is not
// a privilege bypass). Falls back to a full re-login only if the Auth0 session
// is also gone.
function SessionExpiredListener() {
  const { isAuthenticated, user, getIdTokenClaims, getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  useEffect(() => {
    const handler = async () => {
      try {
        if (isAuthenticated && user?.sub) {
          const claims = await getFreshIdTokenClaims({ getIdTokenClaims, getAccessTokenSilently });

          await sessionAuthService.authenticateProsciAdmin(user.sub, user.email, claims?.sid, claims?.__raw);

          // Cookie renewed. Allow future expirations to be handled again and
          // refetch data that failed during the gap — no reload, state preserved.
          resetSessionExpirationHandling();
          queryClient.invalidateQueries();
          return;
        }
      } catch {
        // Re-mint failed (Auth0 session gone or admin no longer valid) — fall
        // through to a full re-login.
      }

      // Recovery not possible. Clear and reload the CURRENT route so
      // ProtectedRoute drives an Auth0 re-login and returns the user here.
      sessionAuthService.clearSession();
      window.location.reload();
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handler);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handler);
  }, [isAuthenticated, user, getIdTokenClaims, getAccessTokenSilently, queryClient]);

  return null;
}

interface AuthProviderProps {
  children: ReactNode;
}

function Auth0ProviderWithNavigate({ children }: AuthProviderProps) {
  const navigate = useNavigate();
  const domain = import.meta.env.VITE_AUTH0_DOMAIN;
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
  const redirectUri = window.location.origin;


  if (!domain || !clientId) {
    throw new Error('Missing Auth0 configuration');
  }

  const authorizationParams: any = {
    redirect_uri: redirectUri,
    scope: "openid profile email offline_access",
    'ext-source': 'portal'
  };

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={authorizationParams}
      onRedirectCallback={(appState) => {
        // Remove the code and state from URL
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.delete('code');
        searchParams.delete('state');
        searchParams.delete('error');
        searchParams.delete('error_description');

        const newUrl = searchParams.toString()
          ? `${window.location.pathname}?${searchParams.toString()}`
          : window.location.pathname;

        // Only allow same-app relative paths (open-redirect guard)
        const returnTo =
          appState?.returnTo &&
          appState.returnTo.startsWith('/') &&
          !appState.returnTo.startsWith('//')
            ? appState.returnTo
            : null;

        // Use React Router navigation
        navigate(returnTo || newUrl || '/users');
      }}
      cacheLocation="localstorage"
      useRefreshTokens={true}
      useRefreshTokensFallback={true}
    >
      <SessionRevokedListener />
      <SessionExpiredListener />
      {children}
    </Auth0Provider>
  );
}

export function AuthProvider({ children }: AuthProviderProps) {
  return <Auth0ProviderWithNavigate>{children}</Auth0ProviderWithNavigate>;
}